import React, { useState, useEffect } from 'react';
import { subscriptionAPI, contentAPI } from '../services/api';
import './Dashboard.css';
import WordPressSites from './WordPressSites';
import TopicManager from './TopicManager';
import QuickPost from './QuickPost';
import Analytics from './Analytics';

function Dashboard({ user, onLogout }) {
  const [showTopicForm, setShowTopicForm] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('dashboard');
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [wordPressSites, setWordPressSites] = useState([]);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewTopicData, setViewTopicData] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [subResponse, topicsResponse] = await Promise.all([
        subscriptionAPI.getSubscription(),
        fetch('https://contentvertex.zealcart.com/api/topics', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        }).then(res => res.json())
      ]);
      
      setSubscription(subResponse.data.subscription);
      
      // Sort by latest and take top 5
      if (topicsResponse.success) {
        const sortedTopics = topicsResponse.topics.sort((a, b) => 
          new Date(b.created_at) - new Date(a.created_at)
        ).slice(0, 5);
        setArticles(sortedTopics);
      }
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewTopic = async (topicId) => {
    try {
      const response = await fetch(`https://contentvertex.zealcart.com/api/topics/${topicId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setViewTopicData(data.topic);
        setShowViewModal(true);
      } else {
        alert('Failed to load topic details');
      }
    } catch (error) {
      alert('Failed to load topic details');
    }
  };

  const formatContentForDisplay = (content) => {
    if (!content) return '';
    
    let formattedContent = content;
    
    formattedContent = formattedContent.replace(/^#{1,6}\s*.+?\n+/, '');
    formattedContent = formattedContent.replace(/^[^\n]+:\s*[^\n]+\n+/, '');
    formattedContent = formattedContent.replace(/^###?\s*(Introduction|INTRODUCTION)\s*\n/im, '');
    formattedContent = formattedContent.replace(/^##?\s*(Introduction|INTRODUCTION)\s*\n/im, '');
    formattedContent = formattedContent.replace(/^####\s*H4:\s*(.+)$/gm, '<h4>$1</h4>');
    formattedContent = formattedContent.replace(/^####\s*(.+)$/gm, '<h4>$1</h4>');
    formattedContent = formattedContent.replace(/^###?\s*(Key Takeaways|Conclusion)\s*$/gim, '<h2>$1</h2>');
    formattedContent = formattedContent.replace(/^###\s*H3:\s*(.+)$/gm, '<h3>$1</h3>');
    formattedContent = formattedContent.replace(/^###\s*(.+)$/gm, '<h3>$1</h3>');
    formattedContent = formattedContent.replace(/^##\s*H2:\s*(.+)$/gm, '<h2>$1</h2>');
    formattedContent = formattedContent.replace(/^##\s*(.+)$/gm, '<h2>$1</h2>');
    formattedContent = formattedContent.replace(/^#\s*H1:\s*(.+)$/gm, '<h1>$1</h1>');
    formattedContent = formattedContent.replace(/^#\s*(.+)$/gm, '<h1>$1</h1>');
    formattedContent = formattedContent.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    formattedContent = formattedContent.replace(/^\* (.+)$/gm, '<li>$1</li>');
    formattedContent = formattedContent.replace(/^- (.+)$/gm, '<li>$1</li>');
    formattedContent = formattedContent.replace(/(<li>.*?<\/li>\s*)+/gs, '<ul>$&</ul>');
    formattedContent = formattedContent.replace(/\n\n/g, '</p><p>');
    formattedContent = '<p>' + formattedContent + '</p>';
    formattedContent = formattedContent.replace(/<p><\/p>/g, '');
    formattedContent = formattedContent.replace(/<p>\s*<h([1-6])/g, '<h$1');
    formattedContent = formattedContent.replace(/<\/h([1-6])>\s*<\/p>/g, '</h$1>');
    formattedContent = formattedContent.replace(/<p>\s*<ul>/g, '<ul>');
    formattedContent = formattedContent.replace(/<\/ul>\s*<\/p>/g, '</ul>');
    
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = formattedContent;
    
    const excludedH2Sections = ['Key Takeaways', 'Conclusion', 'Introduction'];
    let h3Counter = 0;
    let currentH3Number = 0;
    let h4Counter = 0;
    let shouldNumberH3 = false;
    
    Array.from(tempDiv.childNodes).forEach(node => {
      if (node.nodeName === 'H2') {
        h3Counter = 0;
        currentH3Number = 0;
        h4Counter = 0;
        const h2Text = node.textContent.trim();
        shouldNumberH3 = !excludedH2Sections.some(excluded => 
          h2Text.toLowerCase().includes(excluded.toLowerCase())
        );
      }
      
      if (node.nodeName === 'H3') {
        const h3Text = node.textContent.trim();
        const isExcluded = excludedH2Sections.some(excluded => 
          h3Text.toLowerCase().includes(excluded.toLowerCase())
        );
        
        if (shouldNumberH3 && !isExcluded) {
          h3Counter++;
          currentH3Number = h3Counter;
          h4Counter = 0;
          const cleanText = node.textContent.replace(/^\d+\.\s*/, '').trim();
          node.innerHTML = `<span class="heading-number">${h3Counter}.</span> ${cleanText}`;
        }
      }
      
      if (node.nodeName === 'H4' && currentH3Number > 0) {
        h4Counter++;
        const cleanText = node.textContent.replace(/^\d+\.\d+\s*/, '').trim();
        node.innerHTML = `<span class="sub-heading-number">${currentH3Number}.${h4Counter}</span> ${cleanText}`;
      }
    });
    
    return tempDiv.innerHTML;
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-gray-100 text-gray-800',
      processing: 'bg-yellow-100 text-yellow-800',
      generated: 'bg-blue-100 text-blue-800',
      published: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };
    
    return (
      <span className={`status-badge ${colors[status]}`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getCategoryName = (topic) => {
    return topic.category_id ? 'Categorized' : 'Uncategorized';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const loadWordPressSites = async () => {
    try {
      const response = await fetch('https://contentvertex.zealcart.com/api/wordpress-sites', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      setWordPressSites(data.sites);
    } catch (error) {
      console.error('Failed to load WordPress sites:', error);
    }
  };

  const publishToWordPress = async (siteId) => {
    try {
      const response = await subscriptionAPI.publishToWordPress(siteId, {
        title: selectedArticle.title,
        content: selectedArticle.content,
        excerpt: selectedArticle.excerpt,
        meta_description: selectedArticle.meta_description,
        tags: selectedArticle.post_tags
      });
      
      if (response.data.success) {
        alert('Article published to WordPress successfully!');
        setShowPublishModal(false);
      } else {
        alert('Failed to publish: ' + response.data.message);
      }
    } catch (error) {
      alert('Publishing failed');
    }
  };

  if (showTopicForm) {
    return (
      <QuickPost 
        user={user}
        onClose={(redirectTo) => {
          setShowTopicForm(false);
          loadDashboardData();
          if (redirectTo === 'topics') {
            setActivePage('topics');
          }
        }}
      />
    );
  }

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <header className="dashboard-header">
        <div className="container">
          <div className="header-left">
            <img 
              src="/images/ContentVertex-Logo.svg"
              alt="ContentVertex" 
              className="logo" 
            />
            <nav className="nav-menu">
              <span 
                className={`nav-item ${activePage === 'dashboard' ? 'active' : ''}`}
                onClick={() => setActivePage('dashboard')}
              >
                Dashboard
              </span>
              <span 
                className={`nav-item ${activePage === 'analytics' ? 'active' : ''}`}
                onClick={() => setActivePage('analytics')}
              >
                📊 Analytics
              </span>
              <span 
                className={`nav-item ${activePage === 'topics' ? 'active' : ''}`}
                onClick={() => setActivePage('topics')}
              >
                Topic Queue
              </span>
              <span 
                className={`nav-item ${activePage === 'wordpress' ? 'active' : ''}`}
                onClick={() => setActivePage('wordpress')}
              >
                WordPress Sites
              </span>
              <span className="nav-item">Help</span>
            </nav>
          </div>
          <div className="user-profile">
            <span>{user.name}</span>
            <button onClick={onLogout} className="logout-btn">Logout</button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="dashboard-main">
        {activePage === 'analytics' && (
          <Analytics user={user} />
        )}

        {activePage === 'dashboard' && (
          <div className="container">
            {/* Welcome Section */}
            <div className="welcome-section">
              <h2>Welcome back, {user.name}!</h2>
              <button 
                className="generate-btn"
                onClick={() => setShowTopicForm(true)}
              >
                + GENERATE NEW ARTICLE
              </button>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
              <div className="stat-card">
                <h3>Articles Remaining</h3>
                <div className="stat-value">
                  {subscription?.articles_remaining || 0} / {subscription?.articles_limit || 0}
                </div>
              </div>
              
              <div className="stat-card">
                <h3>Total Articles Generated</h3>
                <div className="stat-value">
                  {subscription?.articles_used || 0}
                </div>
              </div>
              
              <div className="stat-card">
                <h3>Your Plan</h3>
                <div className="stat-value plan-name">
                  {subscription?.plan_name || 'Site Launchpad'}
                </div>
              </div>
            </div>

            {/* Articles Table */}
            <div className="articles-section">
              <h3>Your Latest Articles</h3>
              <div className="articles-table">
                <div className="table-header">
                  <div className="col-title">ARTICLE TITLE</div>
                  <div className="col-date">DATE CREATED</div>
                  <div className="col-actions">ACTIONS</div>
                </div>
                
                {articles.length > 0 ? (
                  articles.map((topic) => (
                    <div key={topic.id} className="table-row">
                      <div className="col-title">
                        "{topic.topic_title}"
                      </div>
                      <div className="col-date">
                        {formatDate(topic.created_at)}
                      </div>
                      <div className="col-actions">
                        <button 
                          className="action-btn view-btn"
                          onClick={() => viewTopic(topic.id)}
                        >
                          View
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="no-articles">
                    <p>No articles generated yet. Click "Generate New Article" to get started!</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activePage === 'wordpress' && (
          <WordPressSites user={user} />
        )}
        
        {activePage === 'topics' && (
          <TopicManager user={user} />
        )}
      </main>

      {/* All your existing modals remain the same */}
      {showViewModal && viewTopicData && (
        <div className="add-form-modal">
          <div className="modal-content" style={{maxWidth: '800px', maxHeight: '90vh'}}>
            <div className="modal-header">
              <h3>Article: {viewTopicData.generated_title || viewTopicData.topic_title}</h3>
              <button onClick={() => setShowViewModal(false)}>×</button>
            </div>
            
            <div style={{padding: '24px', overflowY: 'auto'}}>
              <div className="article-meta">
                <div><strong>Site:</strong> {viewTopicData.word_press_site?.site_name || viewTopicData.wordpress_site?.site_name || 'N/A'}</div>
                <div><strong>Category:</strong> {getCategoryName(viewTopicData)}</div>
                <div><strong>Status:</strong> {getStatusBadge(viewTopicData.status)}</div>
                <div><strong>Type:</strong> {viewTopicData.publish_type}</div>
              </div>
              
              {viewTopicData.generated_content ? (
                <div className="article-content">
                  <h2 className="article-title">Generated Article</h2>
                  {viewTopicData.generated_excerpt && (
                    <div className="excerpt">
                      <h4>Excerpt:</h4>
                      <p>{viewTopicData.generated_excerpt}</p>
                    </div>
                  )}
                  <div 
                    className="content" 
                    dangerouslySetInnerHTML={{
                      __html: formatContentForDisplay(viewTopicData.generated_content)
                    }} 
                  />
                  {viewTopicData.generated_tags && viewTopicData.generated_tags.length > 0 && (
                    <div className="tags">
                      <h4>Tags:</h4>
                      <div className="tag-list">
                        {viewTopicData.generated_tags.map((tag, index) => (
                          <span key={index} className="tag">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="no-content">
                  <p>No generated content available. {viewTopicData.status === 'pending' ? 'Process this topic to generate content.' : ''}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showPublishModal && (
        <div className="publish-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Publish to WordPress</h3>
              <button 
                className="close-btn"
                onClick={() => setShowPublishModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="modal-body">
              <p>Select a WordPress site to publish "{selectedArticle?.title}":</p>
              
              {wordPressSites.length === 0 ? (
                <div className="no-sites-message">
                  <p>No WordPress sites connected.</p>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowPublishModal(false);
                      setActivePage('wordpress');
                    }}
                  >
                    Add WordPress Site
                  </button>
                </div>
              ) : (
                <div className="sites-list">
                  {wordPressSites.map(site => (
                    <div key={site.id} className="site-option">
                      <div className="site-info">
                        <h4>{site.site_name}</h4>
                        <p>{site.site_url}</p>
                        <span className={`status ${site.status}`}>
                          {site.status === 'connected' ? '🟢' : '🔴'} {site.status}
                        </span>
                      </div>
                      <button 
                        className="btn btn-primary"
                        onClick={() => publishToWordPress(site.id)}
                        disabled={site.status !== 'connected'}
                      >
                        Publish Here
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <footer className="dashboard-footer">
        <div className="container">
          <p>© 2025 ContentVertex | Terms of Service | Privacy Policy</p>
        </div>
      </footer>
    </div>
  );
}

export default Dashboard;